export type ApiErrorKind = 'bad_request' | 'unauthorized' | 'forbidden' | 'not_found' | 'conflict' |
  'precondition' | 'validation' | 'rate_limit' | 'server' | 'http' | 'network' | 'timeout' | 'cancelled' | 'invalid_response';
export interface ApiFieldError { loc: (string | number)[]; msg: string; type?: string }

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status?: number;
  readonly code?: string;
  readonly retryAfterSeconds?: number;
  readonly validation?: readonly ApiFieldError[];
  readonly context?: { method: string };
  constructor(message: string, details: { kind: ApiErrorKind; status?: number; code?: string;
    retryAfterSeconds?: number; validation?: ApiFieldError[]; context?: { method: string }; cause?: unknown }) {
    super(message, { cause: details.cause });
    this.name = 'ApiError';
    this.kind = details.kind; this.status = details.status; this.code = details.code;
    this.retryAfterSeconds = details.retryAfterSeconds; this.validation = details.validation;
    this.context = details.context;
  }
}

export function getApiErrorKind(error: unknown): ApiErrorKind | undefined {
  if (error instanceof ApiError) return error.kind;
  if (error && typeof error === 'object' && 'name' in error) {
    if (error.name === 'AbortError') return 'cancelled';
    if (error.name === 'TimeoutError') return 'timeout';
  }
  return undefined;
}

function safeMessage(value: unknown, fallback: string): string {
  if (typeof value !== 'string' || !value.trim()) return fallback;
  // Technical error pages and credentials are never user-facing messages.
  if (/traceback|stack trace|<!doctype|<html|<script|\b(?:password|authorization|cookie|access_token|refresh_token|csrf_token|api_key)\s*[:=]|bearer\s+\S+|(?:postgres|postgresql|mysql):\/\//i.test(value)) return fallback;
  return value;
}

export function extractErrorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object') {
    const detail = (payload as { detail?: unknown }).detail;
    if (typeof detail === 'string' && detail.trim()) return safeMessage(detail, fallback);
    if (Array.isArray(detail)) {
      const messages = detail.flatMap(item => item && typeof item === 'object' && typeof item.msg === 'string'
        ? [safeMessage(item.msg, fallback)] : []);
      if (messages.length) return messages.join('; ');
    }

    const error = (payload as { error?: unknown }).error;
    if (typeof error === 'string' && error.trim()) return safeMessage(error, fallback);
    if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') return safeMessage(error.message, fallback);

    const message = (payload as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return safeMessage(message, fallback);
  }
  return fallback;
}

export function responseApiError(response: Response, payload: unknown, fallback: string, context?: { method: string }): ApiError {
  const kinds: Record<number, ApiErrorKind> = { 400: 'bad_request', 401: 'unauthorized', 403: 'forbidden',
    404: 'not_found', 409: 'conflict', 412: 'precondition', 422: 'validation', 429: 'rate_limit' };
  const object = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  const nested = object.error && typeof object.error === 'object' ? object.error as Record<string, unknown> : {};
  const rawCode = nested.code ?? object.code;
  const code = typeof rawCode === 'string' && /^[\w.:-]{1,100}$/.test(rawCode) ? rawCode : undefined;
  const validation = Array.isArray(object.detail) ? object.detail.flatMap(item => {
    if (!item || typeof item !== 'object' || typeof item.msg !== 'string') return [];
    return [{ loc: Array.isArray(item.loc) ? item.loc.filter((v: unknown) => typeof v === 'string' || typeof v === 'number') : [],
      msg: safeMessage(item.msg, fallback), type: typeof item.type === 'string' ? item.type : undefined }];
  }) : undefined;
  const retry = response.headers.get('Retry-After');
  const seconds = retry ? (/^\d+$/.test(retry) ? Number(retry) : Math.ceil((Date.parse(retry) - Date.now()) / 1000)) : undefined;
  return new ApiError(response.status >= 500 ? fallback : extractErrorMessage(payload, fallback), {
    kind: kinds[response.status] ?? (response.status >= 500 ? 'server' : 'http'), status: response.status, code, validation, context,
    retryAfterSeconds: seconds !== undefined && Number.isFinite(seconds) ? Math.max(0, seconds) : undefined,
  });
}

export async function parseJsonSafe<T>(response: Response): Promise<T | null> {
  const alreadyConsumed = response.bodyUsed;
  try {
    return (await response.json()) as T;
  } catch (error) {
    // Legacy callers may assert success before parsing again. Preserve their
    // null result without swallowing an abort or an unread body's stream error.
    if (alreadyConsumed && error instanceof TypeError) return null;
    if (!(error instanceof SyntaxError)) throw error;
    return null;
  }
}

export async function assertOkJson<T>(response: Response, fallback: string): Promise<T> {
  const payload = await parseJsonSafe<T & { detail?: unknown; error?: unknown; message?: unknown }>(response);
  if (!response.ok) {
    throw responseApiError(response, payload, fallback);
  }
  return (payload ?? ({} as T)) as T;
}

export async function assertOk(response: Response, fallback: string): Promise<void> {
  const payload = await parseJsonSafe<{ detail?: unknown; error?: unknown; message?: unknown }>(response);
  if (!response.ok) {
    throw responseApiError(response, payload, fallback);
  }
}
